import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';

export const RevenueConcentrationChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.breakdown) return [];

        const productRevenue: Record<string, number> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'REVENUE' && item.category === 'product_sale') {
                const id = item.entityId as string;
                productRevenue[id] = (productRevenue[id] || 0) + (Number(item.value) || 0);
            }
        }

        const sorted = Object.entries(productRevenue)
            .sort(([, a], [, b]) => b - a);

        const totalRevenue = sorted.reduce((sum, [, val]) => sum + val, 0);
        if (totalRevenue === 0) return [];

        let cumulative = 0;
        return sorted.map(([, revenue], index) => {
            cumulative += revenue;
            return {
                rank: index + 1,
                cumulativePercent: Math.round((cumulative / totalRevenue) * 10000) / 100,
            };
        });
    }, [finance?.breakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="rank"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                            label={{
                                value: t('charts.productRank', 'Product Rank'),
                                position: 'insideBottom',
                                offset: -5,
                                style: CHART_STYLES.axis.tick,
                            }}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}%`}
                            domain={[0, 100]}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}%`,
                                t('charts.cumulativeRevenue', 'Cumulative Revenue'),
                            ]}
                            labelFormatter={(label: number) =>
                                t('charts.productN', 'Product #{{n}}', { n: label })
                            }
                        />
                        <ReferenceLine
                            y={80}
                            stroke={CHART_COLORS.danger}
                            strokeDasharray="4 4"
                            label={{
                                value: '80%',
                                position: 'insideTopRight',
                                style: { fill: CHART_COLORS.danger, fontSize: 10 },
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="cumulativePercent"
                            stroke={CHART_COLORS.primary}
                            fill={CHART_COLORS.primary}
                            fillOpacity={0.15}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
